type CustomerType = 'new' | 'existing';
type ScheduleEvent =
  | 'appt_options_displayed'
  | 'appt_scheduled'
  | 'appt_modified'
  | 'appt_cancelled';

declare global {
  interface Window {
    dataLayer?: Record<string, string>[];
  }
}

const EVENT_NAMES: ScheduleEvent[] = [
  'appt_options_displayed',
  'appt_scheduled',
  'appt_modified',
  'appt_cancelled',
];

const CUSTOMER_TYPE_STORAGE_KEY = 'oa_schedule_customer_type';
const EVENT_STORAGE_PREFIX = 'oa_schedule_tracked_';
const ODR_WIDGET_SELECTOR = '.odr-widget, .odr-g-widget, [data-odr-flow]';

const EVENT_PATTERNS: Record<ScheduleEvent, RegExp[]> = {
  appt_options_displayed: [
    /\b(available|choose|select|pick)\b.{0,40}\b(appointment|time|date|slot)s?\b/i,
    /\bappointment\b.{0,40}\b(options|times|availability)\b/i,
    /\b(next|first)\b.{0,24}\bavailable\b.{0,40}\bappointment\b/i,
  ],
  appt_scheduled: [
    /\b(appointment|booking)\b.{0,40}\b(confirmed|scheduled|booked)\b/i,
    /\bthank you\b.{0,80}\b(appointment|booking)\b/i,
  ],
  appt_modified: [
    /\b(appointment|booking)\b.{0,40}\b(rescheduled|modified|updated|changed)\b/i,
    /\b(reschedule|modify|change)\b.{0,40}\b(confirmed|complete|successful)\b/i,
  ],
  appt_cancelled: [
    /\b(appointment|booking)\b.{0,40}\b(cancelled|canceled)\b/i,
    /\b(cancel|cancellation)\b.{0,40}\b(confirmed|complete|successful)\b/i,
  ],
};

const CUSTOMER_PATTERNS: Record<CustomerType, RegExp[]> = {
  new: [
    /\bnew\b.{0,24}\b(patient|customer|account)\b/i,
    /\bfirst\b.{0,24}\b(time|visit|appointment)\b/i,
    /\bschedule an appointment\b/i,
  ],
  existing: [
    /\b(existing|returning|current)\b.{0,24}\b(patient|customer|account)\b/i,
    /\blog\s?in\b|\bsign\s?in\b/i,
    /\bview\b.{0,20}\breschedule\b.{0,20}\bcancel\b.{0,30}\bappointments?\b/i,
  ],
};

const isSchedulePage = () => {
  return /\/schedule-now\/?$/.test(window.location.pathname);
};

const hasScheduleWidget = () => {
  return Boolean(document.querySelector(ODR_WIDGET_SELECTOR));
};

const getStoredCustomerType = (): CustomerType => {
  const stored = window.sessionStorage.getItem(CUSTOMER_TYPE_STORAGE_KEY);

  return stored === 'existing' ? 'existing' : 'new';
};

const setCustomerType = (customerType: CustomerType) => {
  window.sessionStorage.setItem(CUSTOMER_TYPE_STORAGE_KEY, customerType);
};

const inferCustomerType = (text: string): CustomerType | null => {
  if (CUSTOMER_PATTERNS.existing.some((pattern) => pattern.test(text))) {
    return 'existing';
  }

  if (CUSTOMER_PATTERNS.new.some((pattern) => pattern.test(text))) {
    return 'new';
  }

  return null;
};

const inferCustomerTypeFromElement = (element: Element): CustomerType | null => {
  const selectedInput =
    element instanceof HTMLInputElement &&
    (element.type === 'radio' || element.type === 'checkbox') &&
    element.checked
      ? element
      : null;
  const selectedOption =
    element instanceof HTMLSelectElement
      ? element.selectedOptions.item(0)
      : null;
  const explicitCustomerType = element
    .closest('[data-gtm-customer-type]')
    ?.getAttribute('data-gtm-customer-type');
  const text = [
    explicitCustomerType || '',
    selectedInput?.value || '',
    selectedOption?.textContent || '',
    selectedOption?.value || '',
    readText(element.closest('button, label, .odr-linemenu__item') || element),
  ].join(' ');

  return inferCustomerType(text);
};

const inferEvent = (text: string): ScheduleEvent | null => {
  return (
    EVENT_NAMES.find((eventName) =>
      EVENT_PATTERNS[eventName].some((pattern) => pattern.test(text))
    ) ?? null
  );
};

const pushScheduleEvent = (
  event: ScheduleEvent,
  customerType = getStoredCustomerType()
) => {
  const eventKey = `${EVENT_STORAGE_PREFIX}${event}_${customerType}`;

  if (window.sessionStorage.getItem(eventKey)) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    customer_type: customerType,
  });
  window.sessionStorage.setItem(eventKey, '1');
};

const readText = (element: Element | Document = document) => {
  return (element.textContent || '').replace(/\s+/g, ' ').trim();
};

const trackFromElement = (element: Element) => {
  const eventName = element.getAttribute('data-gtm-appt-event');

  if (!EVENT_NAMES.includes(eventName as ScheduleEvent)) {
    return false;
  }

  const customerTypeAttribute = element.getAttribute('data-gtm-customer-type');
  const customerType =
    customerTypeAttribute === 'existing' || customerTypeAttribute === 'new'
      ? customerTypeAttribute
      : getStoredCustomerType();

  pushScheduleEvent(eventName as ScheduleEvent, customerType);

  return true;
};

const scanDocument = () => {
  if (!hasScheduleWidget()) {
    return;
  }

  const widgetText = Array.from(document.querySelectorAll(ODR_WIDGET_SELECTOR))
    .map((widget) => readText(widget))
    .join(' ');
  const eventName = inferEvent(`${window.location.href} ${widgetText}`);

  if (eventName) {
    pushScheduleEvent(eventName);
  }
};

const scheduleScan = (() => {
  let timeout: number | undefined;

  return () => {
    window.clearTimeout(timeout);
    timeout = window.setTimeout(scanDocument, 250);
  };
})();

export const scheduleTracking = () => {
  if (!isSchedulePage()) {
    return;
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trackingElement = target?.closest('[data-gtm-appt-event]');
    const customerType = target ? inferCustomerTypeFromElement(target) : null;
    const clickedText = target
      ? readText(target.closest('button, .odr-linemenu__item') || target)
      : '';

    if (customerType) {
      setCustomerType(customerType);
    }

    if (trackingElement && trackFromElement(trackingElement)) {
      return;
    }

    const eventName = inferEvent(clickedText);

    if (eventName) {
      pushScheduleEvent(eventName, customerType ?? getStoredCustomerType());
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const customerType = target ? inferCustomerTypeFromElement(target) : null;

    if (customerType) {
      setCustomerType(customerType);
    }
  });

  new MutationObserver(scheduleScan).observe(document.body, {
    childList: true,
    subtree: true,
  });

  window.addEventListener('popstate', scheduleScan);
  scheduleScan();
};
